import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

function Geocoder({
  apiToken,
  language,
  mapboxRef,
  onResult,
  placeholder,
  setGeocoderRef,
  options,
  onClear,
  onSearch,
  onLoading,
  onError
}) {
  const geocoder = useRef(null);
  const elementId = 'geocoder';

  useEffect(() => {
    if (geocoder.current) {
      geocoder.current.setLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (geocoder.current) {
      geocoder.current.setPlaceholder(placeholder);
    }
  }, [placeholder]);

  useEffect(() => {
    geocoder.current = new MapboxGeocoder({
      accessToken: apiToken,
      mapboxgl,
      placeholder,
      flyTo: {
        animate: false
      },
      marker: false,
      language,
      ...options
    });

    geocoder.current.on('result', ({ result }) => {
      onResult({ result });
    });

    geocoder.current.on('clear', onClear);

    geocoder.current.on('results', onSearch);

    geocoder.current.on('loading', onLoading);

    geocoder.current.on('error', onError);

    setGeocoderRef(geocoder.current);

    // This will also work in cases where the mapboxRef is null due to the Geocoder being rendered without a map.
    document
      .getElementById(elementId)
      .appendChild(geocoder.current.onAdd(mapboxRef));

    return function cleanup() {
      if (document.getElementById(elementId)) {
        // When rendering children with ReactDOM.render
        // and passing the rendered element to Mapbox-gl Geocoder
        // we need to manually unmount each of these child elements via React
        // so that React calls each child's lifecycle methods
        // including `unmountComponentAtNode` which removes the child from the DOM.
        ReactDOM.unmountComponentAtNode(document.getElementById(elementId));

        setGeocoderRef(null);
      }
    };
  }, []);

  return <div id={elementId} />;
}

Geocoder.propTypes = {
  apiToken: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  mapboxRef: PropTypes.shape(),
  setGeocoderRef: PropTypes.func,
  onResult: PropTypes.func,
  options: PropTypes.shape(),
  onClear: PropTypes.func,
  onSearch: PropTypes.func,
  onLoading: PropTypes.func,
  onError: PropTypes.func
};

Geocoder.defaultProps = {
  mapboxRef: null,
  setGeocoderRef: () => {},
  onResult: () => {},
  options: {},
  onClear: () => {},
  onSearch: () => {},
  onLoading: () => {},
  onError: () => {}
};

export default Geocoder;
